import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { LinkedListWithLayoutSettings } from '@Components/LinkedListWithLayoutSettings';
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { avvColumns, costBearerColumns, costCenterColumns, storageLocationColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useIsThisEntityNotInUse } from '@Hooks/Permissions';
import { useBadges } from '@Hooks/useBadges';
import { useSaveNestedObject } from '@Hooks/useSaveNestedObject';
import {
    type ApiAccountsGroup,
    type ApiAvv,
    type ApiCostBearer,
    type ApiCostCenter,
    type ApiService,
    type ApiServiceUnitConversion,
    type ApiStorageLocation,
    EnumFrontendLayoutSetting,
    EnumServiceType,
    EnumUnit,
    ServiceLinkLists,
    serviceTypeOptions,
    unitOptions,
} from '@Model';
import {
    FrameEditAccountGroups,
    FrameEditAvv,
    FrameEditCostBearer,
    FrameEditCostCenter,
    FrameEditServiceConversions,
    FrameEditStorageLocation,
} from '@Modules/Stammdaten';
import {
    Button,
    Card,
    Checkbox,
    defaultIconSize,
    EditFrameMode,
    EditFrameShell,
    FlexColumn,
    FlexRow,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    type GroupedSelectOption,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    type Nullable,
    NumberInput,
    Select,
    SplitRow,
    TextInput,
    UnlabeledField,
    useEditSubObject,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedFieldInFrame,
    useLinkedList,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconPlus } from '@tabler/icons-react';
import { useParams } from 'react-router';

const amountOptions: GroupedSelectOption<number>[] = [
    { value: 1, name: '1', label: '1' },
    { value: 2, name: '2', label: '2' },
    { value: 3, name: '3', label: '3' },
    { value: 4, name: '4', label: '4' },
    { value: 5, name: '5', label: '5' },
    { value: 6, name: '6', label: '6' },
    { value: 7, name: '7', label: '7' },
    { value: 8, name: '8', label: '8' },
];

export function FrameEditService(props: IFrameEditShellProps<ApiService>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiService>
            apiResourcePath={`${STAMMDATEN_API}/services`}
            renderer={FrameEditServiceBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.StammdatenServiceUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.StammdatenServiceCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.StammdatenServiceDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditServiceBody(props: IFrameBodyProps<ApiService>) {
    const i18n = useTranslate('');
    const hasPermission = useHasPermission();

    const apiService = props.data;
    const canEdit = props.mode !== EditFrameMode.View;
    const baseApiObjectPath = `${props.apiResourcePath}/${props.data.id}`;

    const binder = useGuiBinder<ApiService>({
        ...props,
        canEdit,
    });

    const { addSubObject: addConversion, requestUpdateSubObject: requestUpdateConversion } = useEditSubObject<
        ApiServiceUnitConversion,
        ApiService
    >(baseApiObjectPath, 'conversions', props.requestUpdateState);

    const canDelete = useIsThisEntityNotInUse(apiService, 'services');

    const requestUpdateAccountGroup = useSaveNestedObject<ApiService, ApiAccountsGroup>(props.requestUpdateState);

    const {
        refetchRef: refetchAvvsRef,
        linkListApi: linkAvvApi,
        linkedObjectOverlayEvents: linkAvvOverlayEvents,
    } = useLinkedList<ApiService, ApiAvv>(baseApiObjectPath, ServiceLinkLists.avv, false, FrameEditAvv);

    const linkCostBearer = useLinkedFieldInFrame(props, 'cost_bearer', FrameEditCostBearer);
    const linkCostCenter = useLinkedFieldInFrame(props, 'cost_center', FrameEditCostCenter);
    const linkStorageLocation = useLinkedFieldInFrame(
        props,
        'storage_location',
        FrameEditStorageLocation,
        hasPermission(Permission.StammdatenStorageLocationCreate),
    );

    const conversionFactorVisible = apiService.conversions.length > 0;

    const serviceBadges = useBadges(apiService);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Leistung', props.mode),
                resourceLabel: 'Leistung',
                ...props.events,
                canDelete,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="headdata" border={'withoutBorder'} label="Informationen zur Leistung" badges={serviceBadges}>
                <Grid columns={12}>
                    <GridColumns spans={[6, 6]}>
                        <Select<Nullable<EnumServiceType>>
                            items={serviceTypeOptions}
                            {...binder.bindToAutoConfirmed<Nullable<EnumServiceType>>('type')}
                            label={i18n('Typ')}
                            clearable
                        />
                        <TextInput {...binder.bindToProp<string>('shorthand')} label={i18n('Suchcode')} />
                    </GridColumns>
                    <GridColumns spans={[6, 6]}>
                        <TextInput {...binder.bindToProp<string>('label')} label={i18n('Bezeichnung')} inputSize={3} />
                        <TextInput
                            {...binder.bindToProp<string>('internal_label')}
                            label={i18n('Interne Bezeichnung')}
                            inputSize={3}
                        />
                    </GridColumns>
                    <GridColumn span={6}>
                        <TextInput
                            {...binder.bindToProp<string>('external_label')}
                            label={i18n('Externe Bezeichnung')}
                            inputSize={3}
                        />
                    </GridColumn>
                    <GridColumns spans={[3, 3, 3, 3]}>
                        <NumberInput
                            {...binder.bindToProp<number>('price_per_unit')}
                            decimals={apiService.decimal_places_price}
                            label={i18n('Preis pro Einheit in €')}
                        />
                        <Select
                            items={unitOptions}
                            {...binder.bindToAutoConfirmed<EnumUnit>('unit')}
                            label={i18n('Einheit')}
                        />
                        <Select
                            {...binder.bindToAutoConfirmed<number>('decimal_places_price')}
                            label={i18n('Nachkomma Preis')}
                            items={amountOptions}
                        />
                        <Select
                            {...binder.bindToAutoConfirmed<number>('decimal_places_amount')}
                            label={i18n('Nachkomma Menge')}
                            items={amountOptions}
                        />
                    </GridColumns>
                </Grid>
            </Card>
            <Card
                name="Umrechnungsfaktor"
                label="Umrechnungsfaktor"
                closed={!conversionFactorVisible}
                collapsable={conversionFactorVisible}
                additionalHeaderAlignment="left"
                additionalHeaderOptions={
                    !conversionFactorVisible && (
                        <Button
                            appearance="outlined"
                            size="xxs"
                            type="square"
                            onClick={addConversion}
                            locked={!canEdit}
                            label="Umrechnungsfaktor hinzufügen"
                            className="ml-1"
                        >
                            <IconPlus size={defaultIconSize} />
                        </Button>
                    )
                }
            >
                {apiService.conversions.map((row) => (
                    <FrameEditServiceConversions
                        key={row.id}
                        data={row}
                        meta={undefined}
                        apiResourcePath={`${baseApiObjectPath}/conversions`}
                        requestUpdateState={(u) => requestUpdateConversion(u, row.id)}
                        stateQueueDepth={props.stateQueueDepth}
                        refetchData={props.refetchData}
                        canEdit={canEdit}
                    />
                ))}
            </Card>
            <Card name="" label="Leistungsparameter">
                <FlexColumn align="flex-start">
                    <Button
                        appearance="outlined"
                        onClick={() => alert('TODO: Leistungsparameter modal öffnen')}
                        locked={!canEdit}
                    >
                        <IconPlus size={defaultIconSize} />
                        &nbsp;Leistungsparameter hinzufügen
                    </Button>
                </FlexColumn>
            </Card>
            <Card name="costs" label="FiBu" border="withoutBorder">
                <Grid>
                    <GridColumns spans={[6, 3, 3]}>
                        <LinkedFieldWithLayoutSettings<ApiStorageLocation>
                            layoutSettingsContext={EnumFrontendLayoutSetting.StorageLocationsTableLayout}
                            {...binder.bindToLinkProperty(linkStorageLocation.linkApi)}
                            {...linkStorageLocation.events}
                            columns={storageLocationColumns}
                            label={i18n('Lagerort')}
                        />
                        <FlexRow align="center" height="100%" gap="xl">
                            <UnlabeledField>
                                <Checkbox
                                    {...binder.bindToAutoConfirmed('reverse_charge')}
                                    label={i18n('Reverse Charge')}
                                />
                            </UnlabeledField>
                            <UnlabeledField>
                                <Checkbox
                                    {...binder.bindToAutoConfirmed('recalculate_price_on_faktura')}
                                    label={i18n('Preisfindung bei Faktura')}
                                />
                            </UnlabeledField>
                        </FlexRow>
                    </GridColumns>
                    <GridColumns spans={[6, 6]}>
                        <LinkedFieldWithLayoutSettings<ApiCostCenter>
                            layoutSettingsContext={EnumFrontendLayoutSetting.CostCentersTableLayout}
                            {...binder.bindToLinkProperty(linkCostCenter.linkApi)}
                            {...linkCostCenter.events}
                            columns={costCenterColumns}
                            label={i18n('Kostenstelle')}
                        />
                        <LinkedFieldWithLayoutSettings<ApiCostBearer>
                            layoutSettingsContext={EnumFrontendLayoutSetting.CostBearersTableLayout}
                            {...binder.bindToLinkProperty(linkCostBearer.linkApi)}
                            {...linkCostBearer.events}
                            columns={costBearerColumns}
                            label={i18n('Kostenträger')}
                        />
                    </GridColumns>
                </Grid>
            </Card>
            <Card name="Konten">
                <SplitRow
                    columns={[
                        {
                            headline: 'Erlöskonten',
                            content: (
                                <>
                                    <FrameEditAccountGroups
                                        data={apiService.inland_revenue_account}
                                        meta={undefined}
                                        canEdit={canEdit}
                                        binder={binder}
                                        accountGroupPropertyName="inland_revenue_account"
                                        apiResourcePath={baseApiObjectPath}
                                        stateQueueDepth={props.stateQueueDepth}
                                        requestUpdateState={(r) =>
                                            requestUpdateAccountGroup(r, 'inland_revenue_account')
                                        }
                                        refetchData={props.refetchData}
                                    />
                                    <FrameEditAccountGroups
                                        data={apiService.eec_revenue_account}
                                        meta={undefined}
                                        canEdit={canEdit}
                                        binder={binder}
                                        accountGroupPropertyName="eec_revenue_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) => requestUpdateAccountGroup(r, 'eec_revenue_account')}
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                    />
                                    <FrameEditAccountGroups
                                        data={apiService.abroad_revenue_account}
                                        meta={undefined}
                                        canEdit={canEdit}
                                        binder={binder}
                                        accountGroupPropertyName="abroad_revenue_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) =>
                                            requestUpdateAccountGroup(r, 'abroad_revenue_account')
                                        }
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                    />
                                    <FrameEditAccountGroups
                                        data={apiService.enterprise_revenue_account}
                                        meta={undefined}
                                        canEdit={canEdit}
                                        binder={binder}
                                        accountGroupPropertyName="enterprise_revenue_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) =>
                                            requestUpdateAccountGroup(r, 'enterprise_revenue_account')
                                        }
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                    />
                                    <FrameEditAccountGroups
                                        data={apiService.tax_free_revenue_account}
                                        meta={undefined}
                                        canEdit={canEdit}
                                        binder={binder}
                                        accountGroupPropertyName="tax_free_revenue_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) =>
                                            requestUpdateAccountGroup(r, 'tax_free_revenue_account')
                                        }
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                    />
                                    <FrameEditAccountGroups
                                        data={apiService.reverse_charge_revenue_account}
                                        meta={undefined}
                                        canEdit={canEdit && apiService.reverse_charge}
                                        binder={binder}
                                        accountGroupPropertyName="reverse_charge_revenue_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) =>
                                            requestUpdateAccountGroup(r, 'reverse_charge_revenue_account')
                                        }
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                        infoTooltip={
                                            !apiService.reverse_charge
                                                ? 'Bitte den Leistungsparameter "Reverse Charge" aktivieren'
                                                : undefined
                                        }
                                    />
                                </>
                            ),
                        },
                        {
                            headline: 'Einsatzkonten',
                            content: (
                                <>
                                    <FrameEditAccountGroups
                                        data={apiService.inland_expense_account}
                                        meta={undefined}
                                        canEdit={canEdit}
                                        binder={binder}
                                        accountGroupPropertyName="inland_expense_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) =>
                                            requestUpdateAccountGroup(r, 'inland_expense_account')
                                        }
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                    />
                                    <FrameEditAccountGroups
                                        data={apiService.eec_expense_account}
                                        meta={undefined}
                                        canEdit={canEdit}
                                        binder={binder}
                                        accountGroupPropertyName="eec_expense_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) => requestUpdateAccountGroup(r, 'eec_expense_account')}
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                    />
                                    <FrameEditAccountGroups
                                        data={apiService.abroad_expense_account}
                                        meta={undefined}
                                        canEdit={canEdit}
                                        binder={binder}
                                        accountGroupPropertyName="abroad_expense_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) =>
                                            requestUpdateAccountGroup(r, 'abroad_expense_account')
                                        }
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                    />
                                    <FrameEditAccountGroups
                                        data={apiService.enterprise_expense_account}
                                        meta={undefined}
                                        canEdit={canEdit}
                                        binder={binder}
                                        accountGroupPropertyName="enterprise_expense_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) =>
                                            requestUpdateAccountGroup(r, 'enterprise_expense_account')
                                        }
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                    />
                                    <FrameEditAccountGroups
                                        data={apiService.tax_free_expense_account}
                                        meta={undefined}
                                        canEdit={canEdit}
                                        binder={binder}
                                        accountGroupPropertyName="tax_free_expense_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) =>
                                            requestUpdateAccountGroup(r, 'tax_free_expense_account')
                                        }
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                    />
                                    <FrameEditAccountGroups
                                        data={apiService.reverse_charge_expense_account}
                                        meta={undefined}
                                        canEdit={canEdit && apiService.reverse_charge}
                                        binder={binder}
                                        accountGroupPropertyName="reverse_charge_expense_account"
                                        apiResourcePath={baseApiObjectPath}
                                        requestUpdateState={(r) =>
                                            requestUpdateAccountGroup(r, 'reverse_charge_expense_account')
                                        }
                                        stateQueueDepth={props.stateQueueDepth}
                                        refetchData={props.refetchData}
                                        infoTooltip={
                                            !apiService.reverse_charge
                                                ? 'Bitte den Leistungsparameter "Reverse Charge" aktivieren'
                                                : undefined
                                        }
                                    />
                                </>
                            ),
                        },
                    ]}
                />
            </Card>
            <Card name="WasteCodes" label="AVV" maxWidth={'80rem'}>
                <LinkedListWithLayoutSettings<ApiAvv>
                    layoutSettingsContext={EnumFrontendLayoutSetting.AvvsTableLayout}
                    layoutSettingsModalContext={EnumFrontendLayoutSetting.AvvsTableLayout}
                    {...binder.bindToLinkList(linkAvvApi)}
                    {...linkAvvOverlayEvents}
                    refetchRef={refetchAvvsRef}
                    columns={avvColumns}
                    label="AVV"
                />
            </Card>
        </EditFrameShell>
    );
}
